<div class="box">
  <div class="box-row header mb-2">
    <app-header-bar
      [backRoute]="'/devices'"
      [title]="device?.uniqueIdentifier || 'scanner.title'"
      [autoTranslateTitle]="!device?.uniqueIdentifier"
    ></app-header-bar>
  </div>

  <app-loading-indicator [loading]="isLoading"></app-loading-indicator>

  @if (device) {
    <div class="card mb-3">
      <div class="card-body">
        <div
          class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3"
        >
          <div>
            <h5 class="card-title mb-1">
              {{ 'scanner.overview.title' | translate }}
            </h5>
            <p class="text-muted mb-0">
              {{ 'scanner.overview.subtitle' | translate }}
            </p>
          </div>
          <div
            [innerHTML]="device.connectionStatus | toScannerStatusBadge"
          ></div>
        </div>

        <div class="row g-3">
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'scanner.serialNr' | translate
            }}</small>
            <div class="fw-semibold">{{ device.uniqueIdentifier }}</div>
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'scanner.status' | translate
            }}</small>
            <div
              [innerHTML]="device.connectionStatus | toScannerStatusBadge"
            ></div>
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'scanner.address' | translate
            }}</small>
            <div class="fw-semibold">{{ device.address }}</div>
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'scanner.lastConnection' | translate
            }}</small>
            <div
              class="fw-semibold"
              [innerHTML]="device.lastConnection | timeAgo"
            ></div>
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'createdAt' | translate
            }}</small>
            <div class="fw-semibold">{{ device.createdAt | dateTime }}</div>
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'scanner.maintenance.status' | translate
            }}</small>
            @if (hasDisplayValue(device.maintenanceStatus)) {
              <div
                [innerHTML]="
                  device.maintenanceStatus
                    | toScannerDeviceMaintenanceStatusBadge
                "
              ></div>
            } @else {
              <div class="fw-semibold">-</div>
            }
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'scanner.maintenance.softwareStatus' | translate
            }}</small>
            @if (hasDisplayValue(device.softwareStatus)) {
              <div
                [innerHTML]="
                  device.softwareStatus | toScannerSoftwareStatusBadge
                "
              ></div>
            } @else {
              <div class="fw-semibold">-</div>
            }
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase">{{
              'scanner.maintenance.firmwareBundleVersion' | translate
            }}</small>
            <div class="fw-semibold">
              {{ device.firmwareBundleVersion || '-' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="accordion" id="scanner-detail-accordion">
      <div class="accordion-item">
        <h2 class="accordion-header" id="maintenance-heading">
          <button
            class="accordion-button"
            [class.collapsed]="!isSectionOpen('maintenance')"
            type="button"
            (click)="toggleSection('maintenance')"
            [attr.aria-expanded]="isSectionOpen('maintenance')"
            aria-controls="maintenance-body"
          >
            {{ 'scanner.detailSections.maintenance' | translate }}
          </button>
        </h2>
        <div
          id="maintenance-body"
          class="accordion-collapse collapse"
          [class.show]="isSectionOpen('maintenance')"
          aria-labelledby="maintenance-heading"
        >
          <div class="accordion-body">
            <div class="row g-3">
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">
                  {{ 'scanner.maintenance.status' | translate }}
                </h6>
                @if (hasDisplayValue(device.maintenanceStatus)) {
                  <div
                    [innerHTML]="
                      device.maintenanceStatus
                        | toScannerDeviceMaintenanceStatusBadge
                    "
                  ></div>
                } @else {
                  -
                }
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">
                  {{ 'scanner.maintenance.battery' | translate }}
                </h6>
                <div
                  [innerHTML]="
                    device.isBatteryOk | toScannerSingleMaintenanceCheckBadge
                  "
                ></div>
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">
                  {{ 'scanner.maintenance.batteryHealthPercent' | translate }}
                  @if (hasBatteryHealthWarning()) {
                    <span class="badge bg-warning text-dark ms-2">
                      {{
                        'scanner.maintenance.warningTriggerValues.batteryHealthPercent'
                          | translate
                      }}
                    </span>
                  }
                </h6>
                @if (hasDisplayValue(device.batteryHealthPercent)) {
                  <div [class]="hasBatteryHealthWarning() ? 'text-danger fw-semibold' : ''">
                    {{ device.batteryHealthPercent | number: '1.0-1' }}%
                  </div>
                } @else if (hasBatteryHealthMissingData()) {
                  <div>
                    <span class="badge rounded-pill text-bg-light d-inline-flex align-items-center">
                      <span class="dot inactive"></span>
                      {{ 'scanner.maintenance.statusValues.missing_data' | translate }}
                    </span>
                  </div>
                } @else {
                  -
                }
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">
                  {{ 'scanner.chargingCycles' | translate }}
                  @if (hasChargingCyclesWarning()) {
                    <span class="badge bg-warning text-dark ms-2">
                      {{
                        'scanner.maintenance.warningTriggerValues.chargingCycles'
                          | translate
                      }}
                    </span>
                  }
                </h6>
                @if (hasDisplayValue(device.displayedChargingCycles)) {
                  <div
                    [class]="hasChargingCyclesWarning() ? 'text-danger fw-semibold' : ''"
                  >
                    {{ device.displayedChargingCycles }}
                  </div>
                } @else {
                  -
                }
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">
                  {{ 'scanner.maintenance.scanEngine' | translate }}
                </h6>
                <div
                  [innerHTML]="
                    device.isScanEngineOk | toScannerSingleMaintenanceCheckBadge
                  "
                ></div>
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">
                  {{ 'scanner.maintenance.dataUptodate' | translate }}
                </h6>
                <div
                  [innerHTML]="
                    device.isDataUpToDate | toScannerSingleMaintenanceCheckBadge
                  "
                ></div>
              </div>
              @if (hasSoftwareData()) {
                <div class="col-12 col-md-6">
                  <h6 class="fw-bold mb-1">
                    {{ 'scanner.maintenance.softwareStatus' | translate }}
                  </h6>
                  @if (hasDisplayValue(device.softwareStatus)) {
                    <div
                      [innerHTML]="
                        device.softwareStatus | toScannerSoftwareStatusBadge
                      "
                    ></div>
                  } @else {
                    -
                  }
                </div>
              } @else {
                <div class="col-12">
                  <p class="mb-0 text-muted fst-italic">
                    {{ 'scanner.emptyStates.softwareData' | translate }}
                  </p>
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="accordion-item">
        <h2 class="accordion-header" id="technical-heading">
          <button
            class="accordion-button"
            [class.collapsed]="!isSectionOpen('technical')"
            type="button"
            (click)="toggleSection('technical')"
            [attr.aria-expanded]="isSectionOpen('technical')"
            aria-controls="technical-body"
          >
            {{ 'scanner.detailSections.technical' | translate }}
          </button>
        </h2>
        <div
          id="technical-body"
          class="accordion-collapse collapse"
          [class.show]="isSectionOpen('technical')"
          aria-labelledby="technical-heading"
        >
          <div class="accordion-body">
            @if (hasTechnicalData()) {
              <div class="row g-3">
                @for (field of firmwareVersionFields; track field.key) {
                  @if (hasDisplayValue(firmwareVersionValue(field.key))) {
                    <div class="col-12 col-md-6">
                      <h6 class="fw-bold mb-1">
                        {{ field.label | translate }}
                      </h6>
                      <div>{{ firmwareVersionValue(field.key) }}</div>
                    </div>
                  }
                }
              </div>
            } @else {
              <p class="mb-0 text-muted fst-italic">
                {{ 'scanner.emptyStates.softwareData' | translate }}
              </p>
            }
          </div>
        </div>
      </div>

      <div class="accordion-item">
        <h2 class="accordion-header" id="operations-heading">
          <button
            class="accordion-button"
            [class.collapsed]="!isSectionOpen('operations')"
            type="button"
            (click)="toggleSection('operations')"
            [attr.aria-expanded]="isSectionOpen('operations')"
            aria-controls="operations-body"
          >
            {{ 'scanner.detailSections.operationData' | translate }}
          </button>
        </h2>
        <div
          id="operations-body"
          class="accordion-collapse collapse"
          [class.show]="isSectionOpen('operations')"
          aria-labelledby="operations-heading"
        >
          <div class="accordion-body">
            @if (hasOperationData()) {
              <div class="row g-3">
                @for (field of operationDataFields; track field.key) {
                  @if (
                    shouldDisplayOperationDataField(field.key) &&
                    hasDisplayValue(displayOperationDataValue(field.key))
                  ) {
                    <div class="col-12 col-md-6">
                      <h6 class="fw-bold mb-1">
                        {{ field.label | translate }}
                      </h6>
                      <div>{{ displayOperationDataValue(field.key) }}</div>
                    </div>
                  }
                }
              </div>
            } @else {
              <p class="mb-0 text-muted fst-italic">
                {{ 'scanner.emptyStates.operationData' | translate }}
              </p>
            }
          </div>
        </div>
      </div>

      <div class="accordion-item">
        <h2 class="accordion-header" id="warranty-heading">
          <button
            class="accordion-button"
            [class.collapsed]="!isSectionOpen('warranty')"
            type="button"
            (click)="toggleSection('warranty')"
            [attr.aria-expanded]="isSectionOpen('warranty')"
            aria-controls="warranty-body"
          >
            {{ 'scanner.detailSections.warrantyCare' | translate }}
          </button>
        </h2>
        <div
          id="warranty-body"
          class="accordion-collapse collapse"
          [class.show]="isSectionOpen('warranty')"
          aria-labelledby="warranty-heading"
        >
          <div class="accordion-body">
            @if (hasWarrantyOrCareData()) {
              <div class="row g-3">
                <div class="col-12 col-md-6">
                  <h6 class="fw-bold mb-1">
                    {{ 'scanner.warrantyBaseEndDate' | translate }}
                  </h6>
                  <div>{{ device.warrantyBaseEndDate | dateTime }}</div>
                </div>
                @if (showFullWarrantyDates()) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">
                      {{ 'scanner.warrantyFullEndDate' | translate }}
                    </h6>
                    <div>{{ device.warrantyFullEndDate | dateTime }}</div>
                  </div>
                }

                @if (device.careArticles?.length) {
                  <div class="col-12">
                    <h6 class="fw-bold mb-1">
                      {{ 'scanner.careArticles' | translate }}
                    </h6>
                    <ul class="mb-0">
                      @for (article of openCareArticles(); track article.id) {
                        <li>
                          <b>{{ article.purchasedTitle }}</b>
                          ({{
                            'scanner.careArticleDuration'
                              | translate
                                : {
                                    value: article.months,
                                    date:
                                      expiryDateMap()[article.id] | datePipe,
                                  }
                          }})
                        </li>
                      }
                      @for (
                        article of expiredCareArticles();
                        track article.id
                      ) {
                        <li>
                          <b
                            ><s>{{ article.purchasedDate }}</s></b
                          >
                          ({{
                            'scanner.careArticleDuration'
                              | translate
                                : {
                                    value: article.months,
                                    date:
                                      expiryDateMap()[article.id] | datePipe,
                                  }
                          }})
                        </li>
                      }
                      @for (
                        article of appliedCareArticles();
                        track article.id
                      ) {
                        <li>
                          @if (article.applicationDate) {
                            <i
                              ><b>{{ article.purchasedTitle }}</b> ({{
                                'scanner.careArticleDuration'
                                  | translate
                                    : {
                                        value: article.months,
                                        date:
                                          expiryDateMap()[article.id]
                                          | datePipe,
                                      }
                              }}) [{{
                                'scanner.careArticleAppliedDate'
                                  | translate
                                    : {
                                        date:
                                          article.applicationDate! | datePipe,
                                      }
                              }}]</i
                            >
                          } @else {
                            <i
                              ><b>{{ article.purchasedTitle }}</b> ({{
                                'scanner.careArticleDuration'
                                  | translate
                                    : {
                                        value: article.months,
                                        date:
                                          expiryDateMap()[article.id]
                                          | datePipe,
                                      }
                              }}) [{{
                                'scanner.careArticleApplied' | translate
                              }}]</i
                            >
                          }
                        </li>
                      }
                      @for (article of warrantyExtensions(); track article.id) {
                        <li>
                          <b>{{ article.purchasedTitle }}</b>
                          ({{
                            'scanner.careArticleMonths'
                              | translate: { months: article.months }
                          }})
                        </li>
                      }
                    </ul>
                  </div>
                }
              </div>
            } @else {
              <p class="mb-0 text-muted fst-italic">
                {{ 'scanner.emptyStates.warrantyCare' | translate }}
              </p>
            }
          </div>
        </div>
      </div>

      @if (isAdmin) {
        <div class="accordion-item">
          <h2 class="accordion-header" id="history-heading">
            <button
              class="accordion-button"
              [class.collapsed]="!isSectionOpen('history')"
              type="button"
              (click)="toggleSection('history')"
              [attr.aria-expanded]="isSectionOpen('history')"
              aria-controls="history-body"
            >
              {{ 'deviceHistory.title' | translate }}
            </button>
          </h2>
          <div
            id="history-body"
            class="accordion-collapse collapse"
            [class.show]="isSectionOpen('history')"
            aria-labelledby="history-heading"
          >
            <div class="accordion-body">
              <app-device-history-list
                [historyItems]="historyItems"
              ></app-device-history-list>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <app-error [error]="error"></app-error>
</div>
